import { ChangeDetectionStrategy, Component, HostBinding, Input, ViewEncapsulation } from '@angular/core';
import { FiProfileSizes, IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { IChatConversation, IChatConversationMessage } from '../../../fi-chat';
import { FiChatConversationMessageType } from '@fi-shared/fi-chat/fi-chat-conversation-message-type';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';

@Component({
    selector: 'fi-conversation-system-message',
    templateUrl: './conversation-system-message.component.html',
    styleUrl: './conversation-system-message.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-conversation-system-message' },
    standalone: false,
})
export class FiConversationSystemMessage {
    @Input({ required: true }) conversationMessage: IChatConversationMessage;
    @Input({ required: true }) isOutgoingMessage: boolean;
    @Input({ required: true }) isUserProfileImageShown: boolean;
    @Input({ required: true }) isCreatedByUserNameShown: boolean;
    @Input({ required: true }) conversation: IChatConversation;
    protected readonly fiProfileSizes = FiProfileSizes;
    protected readonly FiInfoIconMode = FiInfoIconMode;
    private readonly maxDisplayedProfiles: number = 5;

    constructor(
        private fiChatHelperService: FiChatHelperService,
        private appTextService: AppTextService,
        private fiUserDataService: FiUserDataService,
        private sharedActionsService: SharedActionsBaseService,
    ) {}

    @HostBinding('class') get conversationSystemMessageClass(): string {
        return [
            this.isOutgoingMessage ? 'outgoing-message' : '',
            !this.isOutgoingMessage ? 'incoming-message' : '',
            this.isMessageDeleted() ? 'message-deleted' : '',
            this.isUserProfileImageShown ? 'last-message' : '',
        ]
            .filter(Boolean)
            .join(' ');
    }

    protected isCurrentUserAddedToGroup(): boolean {
        return this.fiChatHelperService.isCurrentUserAddedToGroup(this.conversationMessage);
    }

    protected isCurrentUser(userId: number): boolean {
        return userId === this.fiUserDataService.getUserId();
    }

    protected getUserNameText(userName: string, isUserNameCommaShown: boolean): string {
        return `${userName}${isUserNameCommaShown ? ', ' : ''}`;
    }

    protected openUserViewDialog(userId: number): void {
        this.sharedActionsService.openUserViewPopup(userId);
    }

    protected getSystemMessage(): string {
        return this.fiChatHelperService.getSystemMessage(this.conversationMessage);
    }

    protected isGroupStartMessage(): boolean {
        return this.conversationMessage.Type === FiChatConversationMessageType.GroupStart;
    }

    protected isMessageDeleted(): boolean {
        return this.conversationMessage.Type === FiChatConversationMessageType.Deleted;
    }

    protected isMultipleTargetsSystemMessage(): boolean {
        return (
            this.conversationMessage.Type === FiChatConversationMessageType.GroupAddUser ||
            this.conversationMessage.Type === FiChatConversationMessageType.GroupRemoveUser
        );
    }

    // protected isNotLastTargetUser(targetUserIndex: number): boolean {
    //     return targetUserIndex !== this.conversationMessage.TargetUsers.length - 1;
    // }

    protected getAddedCurrentUserLabel(isLast: boolean, isFirst: boolean): string {
        let label = this.appTextService.get('common.you');
        if (!isFirst) {
            label = label.toLowerCase();
        }
        return `${label}${isLast ? '' : ', '}`;
    }

    protected getDeletedMessageLabel(): string {
        if (this.isOutgoingMessage) {
            return this.appTextService.get('chat.conversation_system_message_delete_outgoing');
        }

        return this.appTextService.get('chat.conversation_system_message_delete_incoming', {
            '{0}': this.conversationMessage.UserProfile.FullName,
        });
    }

    protected getGroupCreatedUsers(): IUserProfile[] {
        const groupUsers: IUserProfile[] = this.getGroupUsersToDisplay();

        if (groupUsers.length <= this.maxDisplayedProfiles) {
            return groupUsers;
        }

        groupUsers.length = this.maxDisplayedProfiles;
        return groupUsers;
    }

    protected getExtraGroupCreatedUsers(): IUserProfile[] {
        const groupUsers: IUserProfile[] = this.getGroupUsersToDisplay();

        if (groupUsers.length <= this.maxDisplayedProfiles) {
            return [];
        }

        return groupUsers.slice(this.maxDisplayedProfiles);
    }

    protected getAdditionalUsersCount(): number {
        const groupUsers: IUserProfile[] = this.getGroupUsersToDisplay();

        if (groupUsers.length <= this.maxDisplayedProfiles) {
            return 0;
        }

        return groupUsers.length - this.maxDisplayedProfiles;
    }

    protected getGroupStartMessageLabel(): string {
        if (this.isOutgoingMessage) {
            return this.appTextService.get('chat.conversation_system_message_group_create_outgoing');
        }

        return this.appTextService.get('chat.conversation_system_message_group_create_incoming', {
            '{0}': this.conversationMessage.UserProfile.FullName,
        });
    }

    protected getCreatedByUserName() {
        return this.fiChatHelperService.getCreatedByUserName(this.conversationMessage);
    }

    private getGroupUsersToDisplay(): IUserProfile[] {
        const groupUsers: IUserProfile[] = [...this.conversationMessage.TargetUsers].filter(
            (item: IUserProfile, index: number, self: IUserProfile[]) =>
                self.findIndex((user: IUserProfile) => user.Id === item.Id) === index,
        );

        const currentUserIndex: number = groupUsers.findIndex(
            (user: IUserProfile) => user.Id === this.fiUserDataService.getUserId(),
        );
        if (currentUserIndex !== -1) {
            groupUsers.splice(currentUserIndex, 1);
        }
        return groupUsers;
    }
}
